import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  memo,
} from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as Chevron } from '../../svg/chevron-selector-vertical-dot-svgrepo-com.svg';

const AssemblyQuickDropdown = ({
  assemblies,
  onApply,
  className,
  title,
  onClick,
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const assembliesRef = useRef(assemblies);
  assembliesRef.current = assemblies;
  const highlightedIndexRef = useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0 });

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  const toggleOpen = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      setHighlightedIndex(-1);
      setTimeout(updatePosition, 0);
    }
    setIsOpen((prev) => !prev);
  };

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const handleItemClick = (id) => {
    if (onApply) onApplyRef.current(id);
    closeDropdown();
  };

  // Click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeDropdown]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeDropdown();
        triggerRef.current?.focus();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          Math.min(prev + 1, assembliesRef.current.length - 1),
        );
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, -1));
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const idx = highlightedIndexRef.current;
        const asms = assembliesRef.current;
        if (idx >= 0 && idx < asms.length) {
          if (onApply) onApply(asms[idx].id);
          closeDropdown();
        }
        return;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeDropdown]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={className}
        title={title}
        style={{ ...style, ...(isOpen ? { opacity: 1 } : {}) }}
        onClick={(e) => {
          if (onClick) onClick(e);
          toggleOpen(e);
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <Chevron className="tool-icon" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            className="assembly-quick-dropdown-panel"
            style={{
              position: 'fixed',
              top: `${panelPos.top}px`,
              right: `${panelPos.right}px`,
            }}
            role="listbox"
            aria-label="Quick load assembly"
          >
            {assemblies.length === 0 ? (
              <div className="assembly-quick-dropdown-empty">
                No assemblies
              </div>
            ) : (
              assemblies.map((a, index) => (
                <div
                  key={a.id}
                  className={`assembly-quick-dropdown-item${
                    index === highlightedIndex ? ' highlighted' : ''
                  }`}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleItemClick(a.id);
                  }}
                  style={
                    a.params?.Color
                      ? { borderLeft: `6px solid ${a.params.Color}` }
                      : undefined
                  }
                >
                  {a.name || a.id}
                </div>
              ))
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default AssemblyQuickDropdown;
